import { Button } from '@components/Button/';
import EVImg from '@components/EVImg/EVImg';
import Block from '@components/Layout/Block';
import RichText from '@components/RichText';
import { TypeLink } from '@contentful/types/TypeLink';
import { TypeLinkDownload } from '@contentful/types/TypeLinkDownload';
import { color, font, query } from '@design/templateFns';
import { download } from '@util/download';
import { getTestId } from '@util/getTestIds';
import { FC } from 'react';
import styled from 'styled-components';

export interface WideProps {
  backgroundColor?: boolean;
  header?: string;
  eyebrow?: string;
  imgFocalPoint: any;
  alt_text?: string;
  richText?: any;
  links?: TypeLink[] | TypeLinkDownload[];
  reverse?: boolean;
  className?: string;
}

const Wide: FC<WideProps> = ({
  backgroundColor,
  header,
  eyebrow,
  imgFocalPoint,
  alt_text,
  richText,
  links,
  reverse,
  className,
}) => {
  const downloadHandler = async (url, fileName) => {
    await download(url, fileName);
  };

  const dataTestId = eyebrow ? `${getTestId(eyebrow)}` : 'image-block';

  return (
    <Block fullOnMobile className={className}>
      <GridContainer
        data-testid={`${dataTestId}-section`}
        $background={backgroundColor}
        $reverse={reverse}
      >
        <ContentContainer>
          <div>
            {eyebrow && (
              <Eyebrow data-testid={`${dataTestId}-eyebrow`} $background={backgroundColor}>
                {eyebrow}
              </Eyebrow>
            )}
            {header && (
              <Header data-testid={`${dataTestId}-header`} $background={backgroundColor}>
                {header}
              </Header>
            )}
            <Text $background={backgroundColor}>
              <RichText dataTestId={`${dataTestId}-desc`} richText={richText} />
            </Text>
            <ButtonContainer>
              {links &&
                links.map((link, index) => {
                  //check if the contentType is linkDownload
                  if (link.sys.contentType?.sys.id === 'linkDownload') {
                    return (
                      <Button
                        key={index}
                        dataTestId={`${dataTestId}-download-link-${index}`}
                        variant={backgroundColor ? 'tertiary' : 'tertiaryWhite'}
                        label={link.fields.text}
                        target={true}
                        icon="Download"
                        iconPosition="right"
                        onClickHandler={() =>
                          downloadHandler(
                            'https:' + link.fields.downloadableAsset?.fields.file?.url,
                            link.fields.downloadableAsset?.fields.title,
                          )
                        }
                      />
                    );
                  } else if (link.fields) {
                    return (
                      <div key={index}>
                        <Button
                          arrow={true}
                          dataTestId={`${dataTestId}-btn-${index}`}
                          variant={backgroundColor ? 'tertiary' : 'tertiaryWhite'}
                          target={link.fields.external}
                          url={link.fields.url}
                          label={link.fields.text}
                        />
                      </div>
                    );
                  } else {
                    return null;
                  }
                })}
            </ButtonContainer>
          </div>
        </ContentContainer>
        <ImageWrapper $reverse={reverse}>
          <EVImg
            dataTestId={`${dataTestId}-img`}
            imageFocalPointData={imgFocalPoint}
            alt={alt_text}
          />
        </ImageWrapper>
      </GridContainer>
    </Block>
  );
};

export default Wide;

export const GridContainer = styled.div<{
  $reverse?: boolean;
  $background?: boolean | string;
}>`
  background: ${({ $background }) => ($background ? color('secondaryBg') : color('fg'))};

  @media screen and (${query.atLeast('desktop')}) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: inherit;
    align-items: center;
  }

  @media screen and (${query.atLeast('max')}) {
    grid-template-columns: ${({ $reverse }) => ($reverse ? '1fr 2fr' : '2fr 1fr')};
  }
`;

export const ContentContainer = styled.div`
  display: flex;
  align-items: flex-end;
  padding: 60px 20px 30px;

  @media screen and (${query.atLeast('desktop')}) {
    padding: 60px;
  }
`;

export const Header = styled.h2<{ $background?: boolean | string }>`
  ${font('headline-xxs')};
  color: ${({ $background }) => ($background ? color('fg') : color('bg'))};
  margin: 0 0 16px;
  max-width: 743px;
  padding: 0;

  @media screen and (${query.atLeast('desktop')}) {
    ${font('headline-sm')};
    margin: 0 0 28px;
  }
`;

export const Eyebrow = styled.h3<{ $background?: boolean | string }>`
  text-transform: uppercase;
  color: ${({ $background }) => ($background ? color('cta') : color('bg'))};
  ${font('eyebrow-sm')};
  padding-bottom: 15px;
  margin-top: 0;
  @media screen and (${query.atLeast('tablet')}) {
    font-size: 12px;
  }

  @media screen and (${query.atLeast('desktop')}) {
    ${font('eyebrow-md')};
  }
`;

export const Text = styled.div<{ $background?: boolean | string }>`
  ${font('body-sm-light')};
  color: ${({ $background }) => ($background ? color('fg') : color('bg'))};
  margin-bottom: 28px;

  p:last-child {
    margin-bottom: 0;
  }

  @media screen and (${query.atLeast('desktop')}) {
    ${font('body-md-light')};
    margin-bottom: 40px;
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-direction: column;

  @media screen and (${query.atLeast('desktop')}) {
    gap: 15px;
  }
`;

export const ImageWrapper = styled.div<{ $reverse?: boolean }>`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 75%;

  @media screen and (${query.atLeast('desktop')}) {
    height: 100%;
    padding-bottom: 0;
  }

  @media screen and (${query.atLeast('max')}) {
    padding-bottom: 66.6666%;
    height: 0;
    order: ${({ $reverse }) => ($reverse ? '0' : '-1')};
  }
`;
